import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Heart, MessageCircle, Mail, Lock, User, MapPin, Camera, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../../components/ui/textarea';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import API_URL from '../../utils/apiUrl';
import { t } from '../../i18n/t';

// Brand fallback = the site's own domain, never the word "Dating".
const hostBrand = (() => {
  const h = (window.location.hostname || '').replace(/^www\./, '').split('.')[0];
  return h ? h.charAt(0).toUpperCase() + h.slice(1) : '';
})();

// Talk sites are chat sites: no dating heart.
const BrandMark = process.env.REACT_APP_MODE === 'talk' ? MessageCircle : Heart;

const DatingAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, siteId } = useAuth();
  const { lang } = useLanguage();
  const { t } = useTranslation(lang);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [siteName, setSiteName] = useState('');
  
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', username: '', gender: '', lookingFor: '', city: '', age: '', aboutMe: '' });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  useEffect(() => {
    const fetchSiteName = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/sites/config/${siteId}`);
        const name = res.data?.name || '';
        const domainName = window.location.hostname.replace(/^www\./, '').split('.')[0];
        const displayName = domainName.charAt(0).toUpperCase() + domainName.slice(1);
        setSiteName(name && name.toLowerCase() !== 'sex lek' ? name : displayName);
      } catch (e) { /* use default */ }
    };
    fetchSiteName();
  }, [siteId]);

  useEffect(() => {
    const ref = searchParams.get('ref') || localStorage.getItem('referral_code') || '';
    if (ref) {
      setReferralCode(ref);
      localStorage.setItem('referral_code', ref);
    }
  }, [searchParams]);

  const afterAuthTarget = () => {
    const next = searchParams.get('next');
    return next && next.startsWith('/') ? next : '/discover';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(loginData.email, loginData.password, 'user');
    
    if (result.success) {
      toast.success(t('welcomeBack'));
      navigate(afterAuthTarget());
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return toast.error(t('enterYourEmail'));
    setForgotSending(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, {
        email: forgotEmail.trim(),
        site_id: siteId,
      });
      toast.success(t('resetEmailSent'));
      setForgotMode(false);
      setForgotEmail('');
    } catch (e) {
      toast.error(t('somethingWrong'));
    }
    setForgotSending(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.age && (parseInt(registerData.age) < 18 || parseInt(registerData.age) > 120)) {
      toast.error(t('mustBe18Years'));
      return;
    }
    setLoading(true);
    
    const result = await register(
      registerData.email, registerData.password, registerData.username,
      registerData.gender, registerData.lookingFor, referralCode,
      registerData.city, registerData.age ? parseInt(registerData.age) : null, registerData.aboutMe
    );
    
    if (result.success) {
      // Upload avatar if selected
      if (avatarFile && result.token) {
        try {
          const formData = new FormData();
          formData.append('file', avatarFile);
          await axios.post(`${API_URL}/api/auth/upload-avatar`, formData, {
            headers: { Authorization: `Bearer ${result.token}`, 'Content-Type': 'multipart/form-data' }
          });
        } catch (err) { /* avatar upload failed silently */ }
      }
      toast.success(t('accountCreated'));
      navigate(afterAuthTarget());
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  const handleAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(t('imageUnder5mb')); return; }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  return (
    <div className="min-h-screen dating-bg font-dating-body flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-6">
        <div 
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => navigate('/')}
        >
          <BrandMark className="h-8 w-8 text-rose-500 fill-rose-500" />
          <span className="font-dating-heading text-2xl font-bold text-slate-800">
            {siteName || hostBrand}
          </span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md border-0 shadow-2xl" data-testid="auth-card">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-dating-heading text-2xl">{t('welcome')}</CardTitle>
            <CardDescription>{t('signIn')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={searchParams.get('mode') === 'signup' ? 'register' : 'login'} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">{t('login')}</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">{t('signUp')}</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">{t('email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@example.com"
                        className="pl-10"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        data-testid="login-email"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">{t('password')}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        data-testid="login-password"
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full dating-primary-btn mt-6"
                    disabled={loading}
                    data-testid="login-submit"
                  >
                    {loading ? '...' : t('login')}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setForgotMode(true)}
                    className="w-full text-center text-sm font-medium text-rose-600 hover:text-rose-700 underline underline-offset-4 mt-3 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    {t('forgotPassword')}
                  </button>
                </form>

                {forgotMode && (
                  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setForgotMode(false)}>
                    <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()} data-testid="forgot-password-modal">
                      <h3 className="text-lg font-semibold text-slate-800 mb-1">{t('resetPassword')}</h3>
                      <p className="text-xs text-slate-500 mb-4">{t('resetPasswordHelp')}</p>
                      <form onSubmit={handleForgotPassword} className="space-y-3">
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            type="email"
                            placeholder="your@email.com"
                            className="pl-10"
                            value={forgotEmail}
                            onChange={e => setForgotEmail(e.target.value)}
                            required
                            data-testid="forgot-email-input"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button type="button" variant="outline" className="flex-1" onClick={() => setForgotMode(false)}>{t('cancel')}</Button>
                          <Button type="submit" className="flex-1" disabled={forgotSending} data-testid="forgot-submit-btn">
                            {forgotSending ? '...' : t('submit')}
                          </Button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-3">
                  {/* Avatar Upload */}
                  <div className="flex justify-center">
                    <label className="cursor-pointer group" data-testid="avatar-upload">
                      <div className="w-20 h-20 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 group-hover:border-rose-400 flex items-center justify-center overflow-hidden transition-colors">
                        {avatarPreview ? (
                          <img src={avatarPreview} alt="avatar" className="w-full h-full object-cover" />
                        ) : (
                          <Camera className="h-6 w-6 text-slate-400 group-hover:text-rose-400" />
                        )}
                      </div>
                      <p className="text-xs text-center text-slate-400 mt-1">{t('photoOptional')}</p>
                      <input type="file" className="hidden" accept="image/*" onChange={handleAvatarSelect} />
                    </label>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-username">{t('username')}</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="register-username"
                        type="text"
                        placeholder={t('yourNamePlaceholder')}
                        className="pl-10"
                        value={registerData.username}
                        onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                        required
                        data-testid="register-username"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">{t('email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="you@example.com"
                        className="pl-10"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                        data-testid="register-email"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">{t('password')}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        minLength={8}
                        data-testid="register-password"
                      />
                      <p className="text-xs text-slate-400">{t('passwordRule')}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{t('city')}</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder={t('yourCity')}
                          className="pl-10"
                          value={registerData.city}
                          onChange={(e) => setRegisterData({ ...registerData, city: e.target.value })}
                          data-testid="register-city"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('age')}</Label>
                      <Input
                        type="number"
                        placeholder="18+"
                        min={18}
                        max={120}
                        value={registerData.age}
                        onChange={(e) => setRegisterData({ ...registerData, age: e.target.value })}
                        data-testid="register-age"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{t('iAmA')}</Label>
                      <Select value={registerData.gender} onValueChange={(v) => setRegisterData({ ...registerData, gender: v })}>
                        <SelectTrigger data-testid="register-gender">
                          <SelectValue placeholder={t('selectPlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">{t('man')}</SelectItem>
                          <SelectItem value="female">{t('woman')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('lookingFor')}</Label>
                      <Select value={registerData.lookingFor} onValueChange={(v) => setRegisterData({ ...registerData, lookingFor: v })}>
                        <SelectTrigger data-testid="register-looking-for">
                          <SelectValue placeholder={t('selectPlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">{t('men')}</SelectItem>
                          <SelectItem value="female">{t('women')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('aboutMeOptional')}</Label>
                    <Textarea
                      placeholder={t('tellUsAbout')}
                      value={registerData.aboutMe}
                      onChange={(e) => setRegisterData({ ...registerData, aboutMe: e.target.value })}
                      className="resize-none"
                      rows={2}
                      maxLength={300}
                      data-testid="register-about"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full dating-primary-btn mt-4"
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading ? t('creatingAccount') : t('createAccount')}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DatingAuth;
